import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  BellAlertIcon,
  BellSlashIcon,
  XMarkIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { playNotificationChime } from './ClassUpcomingNotificationBanner';

export type NotificationStatus = 'default' | 'granted' | 'denied' | 'unsupported' | 'insecure';

export const getNotificationPermissionStatus = (): NotificationStatus => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  // Chrome requires a secure context (HTTPS or localhost) for Notification API
  if (typeof window.isSecureContext === 'boolean' && !window.isSecureContext) {
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return 'insecure';
    }
  }
  return Notification.permission as NotificationStatus;
};

export const requestNotificationPermission = async (): Promise<NotificationStatus> => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }

  try {
    const res = await Notification.requestPermission();
    if (res === 'granted') {
      playNotificationChime();
      try {
        const notif = new Notification('🔔 Live Class Alerts Enabled!', {
          body: 'You will receive notifications when live lectures start and before scheduled classes.',
          icon: '/logo192.png',
        });
        notif.onclick = () => {
          window.focus();
        };
      } catch {
        // Ignore fallback if constructor restricted
      }
    }
    return res as NotificationStatus;
  } catch (error) {
    console.warn('Failed to request notification permission:', error);
    return Notification.permission as NotificationStatus;
  }
};

interface NotificationPermissionBannerProps {
  role?: 'student' | 'instructor' | 'admin';
  className?: string;
  onPermissionChange?: (status: NotificationStatus) => void;
}

export const NotificationPermissionBanner: React.FC<NotificationPermissionBannerProps> = ({
  role = 'student',
  className = '',
  onPermissionChange,
}) => {
  const [status, setStatus] = useState<NotificationStatus>(() => getNotificationPermissionStatus());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('kreed_notif_banner_dismissed') === 'true';
    } catch {
      return false;
    }
  });
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const checkStatus = () => {
      const s = getNotificationPermissionStatus();
      setStatus(s);
      if (onPermissionChange) onPermissionChange(s);
    };

    checkStatus();

    // Listen to permissions change if supported in Chrome
    if (typeof navigator !== 'undefined' && (navigator as any).permissions) {
      (navigator as any).permissions
        .query({ name: 'notifications' })
        .then((perm: any) => {
          perm.onchange = () => {
            checkStatus();
          };
        })
        .catch(() => {});
    }
  }, [onPermissionChange]);

  const handleRequest = async () => {
    setLoading(true);
    const newStatus = await requestNotificationPermission();
    setStatus(newStatus);
    if (onPermissionChange) onPermissionChange(newStatus);
    setLoading(false);

    if (newStatus === 'denied') {
      setShowHelpModal(true);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('kreed_notif_banner_dismissed', 'true');
    } catch {}
  };

  // If granted or unsupported or already dismissed in this session, don't show the banner
  if (status === 'granted' || status === 'unsupported' || dismissed) {
    return null;
  }

  return (
    <>
      <div
        className={`relative z-20 rounded-2xl p-3.5 sm:p-4 border backdrop-blur-xl shadow-lg transition-all animate-fadeIn ${
          status === 'insecure'
            ? 'bg-amber-950/40 border-amber-500/40 text-amber-200'
            : status === 'denied'
            ? 'bg-slate-900/90 border-slate-700/80 text-slate-300'
            : 'bg-gradient-to-r from-blue-900/80 via-indigo-900/70 to-slate-900/80 border-indigo-400/40 text-white shadow-indigo-950/40'
        } ${className}`}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start sm:items-center space-x-3 min-w-0">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${
                status === 'insecure'
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  : status === 'denied'
                  ? 'bg-slate-800 text-slate-400 border border-slate-700'
                  : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 animate-pulse'
              }`}
            >
              {status === 'denied' ? (
                <BellSlashIcon className="w-5 h-5" />
              ) : (
                <BellAlertIcon className="w-5 h-5" />
              )}
            </div>

            <div className="text-xs sm:text-sm">
              {status === 'insecure' ? (
                <p>
                  <strong className="text-white">Notice:</strong> Browser notifications in Google Chrome require an HTTPS connection or localhost.
                </p>
              ) : status === 'denied' ? (
                <p>
                  <strong className="text-white">Notifications Blocked:</strong> Chrome blocked notifications for this portal. You will not hear alerts when live lectures start.{' '}
                  <button
                    type="button"
                    onClick={() => setShowHelpModal(true)}
                    className="underline text-indigo-300 hover:text-white font-medium"
                  >
                    How to Unblock in Chrome
                  </button>
                </p>
              ) : (
                <div>
                  <p className="font-bold text-white text-xs sm:text-sm">
                    Enable Live Class Alerts
                  </p>
                  <p className="text-[11px] sm:text-xs text-blue-200/90 mt-0.5">
                    {role === 'instructor'
                      ? 'Receive desktop reminders 15 mins before your scheduled lectures.'
                      : 'Get alerted the second your instructor goes live so you never miss a lecture.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2 self-end sm:self-auto flex-shrink-0">
            {status === 'default' && (
              <button
                type="button"
                onClick={handleRequest}
                disabled={loading}
                className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-400 hover:to-blue-500 text-white font-bold text-xs shadow-md transition-all flex items-center space-x-1.5 cursor-pointer disabled:opacity-50"
              >
                <BellIcon className="w-4 h-4" />
                <span>{loading ? 'Asking Chrome...' : 'Allow Alerts in Chrome'}</span>
              </button>
            )}

            {status === 'denied' && (
              <button
                type="button"
                onClick={() => setShowHelpModal(true)}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs border border-slate-700 transition"
              >
                Unblock Guide
              </button>
            )}

            <button
              type="button"
              onClick={handleDismiss}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition"
              title="Dismiss"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Chrome Unblock Instructions Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fadeIn">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-5 shadow-2xl text-white space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center space-x-2 text-indigo-400 font-bold text-sm sm:text-base">
                <BellAlertIcon className="w-5 h-5" />
                <span>Enabling Notifications in Google Chrome</span>
              </div>
              <button
                type="button"
                onClick={() => setShowHelpModal(false)}
                className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs sm:text-sm text-slate-300 leading-relaxed">
              <p>
                Chrome remembers when notification permissions are declined. Follow these 3 quick steps to enable alerts:
              </p>

              <ol className="list-decimal list-inside space-y-2 bg-slate-950/60 p-3.5 rounded-xl border border-slate-800">
                <li>
                  Look at the <strong>address bar</strong> at the very top of your Chrome window.
                </li>
                <li>
                  Click the <strong>Tune / Lock icon (🔒 / 🎛️)</strong> directly to the left of the website URL.
                </li>
                <li>
                  Find <strong>Notifications</strong> and toggle it from <em>Block</em> to <strong className="text-emerald-400">Allow</strong>.
                </li>
                <li>
                  Reload this page.
                </li>
              </ol>

              <div className="flex items-center space-x-2 text-amber-300 text-xs bg-amber-500/10 p-2.5 rounded-lg border border-amber-500/20">
                <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
                <span>Once allowed, you will hear a chime and receive desktop alerts whenever a live class begins.</span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowHelpModal(false);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow transition"
              >
                Done / Reload Page
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Compact Header Bell Button
 * Placed in the top navigation bar of student & instructor portals.
 */
export const HeaderNotificationBell: React.FC = () => {
  const [status, setStatus] = useState<NotificationStatus>(() => getNotificationPermissionStatus());
  const [showTooltip, setShowTooltip] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  useEffect(() => {
    const update = () => setStatus(getNotificationPermissionStatus());
    update();
    if (typeof navigator !== 'undefined' && (navigator as any).permissions) {
      (navigator as any).permissions
        .query({ name: 'notifications' })
        .then((perm: any) => {
          perm.onchange = update;
        })
        .catch(() => {});
    }
  }, []);

  const handleClick = async () => {
    if (status === 'granted') {
      playNotificationChime();
      try {
        new Notification('🔔 Live Class Alerts Active', {
          body: 'Your browser is set up to receive instant alerts when lectures start.',
          icon: '/logo192.png',
        });
      } catch {}
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      return;
    }

    if (status === 'denied') {
      setShowHelpModal(true);
      return;
    }

    // Status is default — request permission
    const newStatus = await requestNotificationPermission();
    setStatus(newStatus);
    if (newStatus === 'denied') {
      setShowHelpModal(true);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
          status === 'granted'
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20'
            : status === 'denied'
            ? 'bg-slate-800/80 border-slate-700 text-slate-400 hover:text-red-300 hover:border-red-500/30'
            : 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/30 animate-pulse'
        }`}
        title={
          status === 'granted'
            ? 'Notifications active (Click to test chime)'
            : status === 'denied'
            ? 'Notifications blocked in Chrome (Click to unblock)'
            : 'Click to enable live class desktop alerts'
        }
      >
        {status === 'granted' ? (
          <>
            <BellIcon className="w-4 h-4 text-emerald-400" />
            <span className="hidden md:inline">Alerts On</span>
          </>
        ) : status === 'denied' ? (
          <>
            <BellSlashIcon className="w-4 h-4 text-slate-400" />
            <span className="hidden md:inline">Alerts Off</span>
          </>
        ) : (
          <>
            <BellAlertIcon className="w-4 h-4 text-indigo-300" />
            <span className="hidden md:inline">Enable Alerts</span>
          </>
        )}
      </button>

      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-slate-900 border border-slate-700 text-white text-[11px] p-2 rounded-xl shadow-xl z-50">
          <p className="flex items-center space-x-1 text-emerald-400 font-semibold mb-1">
            <CheckCircleIcon className="w-3.5 h-3.5" />
            <span>Alerts Enabled!</span>
          </p>
          <p className="text-slate-300">You will receive desktop alerts and a audio chime when classes start.</p>
        </div>
      )}

      {showHelpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fadeIn">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-5 shadow-2xl text-white space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center space-x-2 text-indigo-400 font-bold text-sm">
                <BellAlertIcon className="w-5 h-5" />
                <span>Enable Chrome Notifications</span>
              </div>
              <button
                type="button"
                onClick={() => setShowHelpModal(false)}
                className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2 text-xs text-slate-300">
              <p>Notifications are blocked in Chrome. To unblock:</p>
              <ol className="list-decimal list-inside space-y-1 bg-slate-950 p-3 rounded-lg border border-slate-800">
                <li>Click the <strong>padlock/tune icon (🔒)</strong> left of the URL in Chrome.</li>
                <li>Set <strong>Notifications</strong> to <strong className="text-emerald-400">Allow</strong>.</li>
                <li>Reload the page.</li>
              </ol>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowHelpModal(false);
                  window.location.reload();
                }}
                className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-lg"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
